/// <amd-module name="Search/CrossAccountSearch/CrossAccountSearch" />

import * as React from "react";
import * as Rest_Client from "Search/LegacyWebApi/RestClient/Search";
import * as Resources from "Search/CrossAccountSearch/Resources";
import { EntitySearchResponse } from "Search/LegacyWebApi/Contracts/Search.SharedLegacy";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { IExtendedSearchSectionProps } from "Search/Shared/Base/Types";
import { PageSource } from "Search/Shared/Base/Sources/PageSource";
import { MenuButton, IMenuItem } from "VSSUI/Menu";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

const AccountFilters = "AccountFilters";
const CollectionFilters = "CollectionFilters";

export interface ICrossAccountSearchState {
    menuItems: IMenuItem[]
}

class CrossAccountSearch extends VssComponent<IExtendedSearchSectionProps, ICrossAccountSearchState> {
    private crossAccountSearchRestClient: Rest_Client.ISearchRestClient;
    private pageSource: PageSource;

    constructor(props: IExtendedSearchSectionProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            menuItems: [this.getLoadingSpinner()]
        };
        this.crossAccountSearchRestClient = context.pageContext.getRestClient<Rest_Client.ISearchRestClient>("Legacy.ISearchRestClient");
        this.pageSource = new PageSource(context.pageContext);
    }
    public render(): JSX.Element {
        return (
            <MenuButton
                text={Resources.CrossAccountSearchLabel}
                contextualMenuProps={{
                    menuProps: {
                        id: "search-Accounts-Button",
                        items: this.state.menuItems
                    }
                }} />
        );
    }

    public componentDidMount(): void {
        this.props.defaultSearchText.subscribe(() => {
            this.setState({ menuItems: [this.getLoadingSpinner()] }, () => {
                this.updateMenuItems();
            });
        });
        this.props.selectedTabId.subscribe(() => {
            this.setState({ menuItems: [this.getLoadingSpinner()] }, () => {
                this.updateMenuItems();
            });
        });
        this.updateMenuItems();
    }

    private updateMenuItems = (): void => {
        const searchText = this.props.defaultSearchText.value;
        const tabId = this.props.selectedTabId.value;
        if (searchText) {
            this.postTenantEntityQuery(tabId, searchText)
                .then(response => {
                    const accountFilterName = this.getAccountFilterName(tabId);
                    const accountFilters = response.filterCategories.filter(cat => cat.name === accountFilterName)[0];
                    const crossAccounts = accountFilters ? accountFilters.filters.filter(filter => !filter.selected) : [];
                    if (crossAccounts.length) {
                        this.setState({
                            menuItems: crossAccounts
                                .map<IMenuItem>(filter => {
                                    return {
                                        id: filter.id,
                                        name: filter.name,
                                        onActivate: this.onActivate,
                                        text: filter.name,
                                        secondaryText: filter.resultCount.toString()
                                    }
                                })
                        });
                    } else {
                        this.setState({
                            menuItems: [{
                                id: Resources.CrossAccountSearchZeroResults,
                                text: Resources.CrossAccountSearchZeroResults
                            }]
                        });
                    }
                }, error => {
                    this.setState(
                        {
                            menuItems: [this.getLoadingFailed()]
                        });
                })
        }
    }

    private getAccountFilterName(tabId: string): string {
        switch (tabId) {
            case "code":
                return AccountFilters;
            case "wiki":
                return CollectionFilters;
            default:
                throw new Error("Unexpected entity: " + tabId + " encountered!");
        }
    }

    private postTenantEntityQuery(tabId: string, searchText: string): Promise<EntitySearchResponse> {
        switch (tabId) {
            case "code":
                return this.crossAccountSearchRestClient.postTenantCodeQuery({
                    filters: [],
                    searchText,
                    includeSuggestions: false,
                    searchFilters: {},
                    skipResults: 0,
                    takeResults: 1,
                    sortOptions: [],
                    summarizedHitCountsNeeded: true
                });
            case "wiki":
                return this.crossAccountSearchRestClient.postTenantWikiQuery({
                    filters: [],
                    searchText,
                    includeSuggestions: false,
                    searchFilters: {},
                    skipResults: 0,
                    takeResults: 1,
                    sortOptions: [],
                    summarizedHitCountsNeeded: true
                });
            default:
                return Promise.reject(new Error("Unexpected entity: " + tabId + " encountered!"));
        }
    }

    private getLoadingSpinner = (): IMenuItem => {
        return {
            id: Resources.CrossAccountSearchSpinnerLabel,
            text: Resources.CrossAccountSearchSpinnerLabel,
            renderMenuItem: (): JSX.Element => {
                return <Spinner
                    className="cross-account-spinner"
                    label={Resources.CrossAccountSearchSpinnerLabel}
                    size={SpinnerSize.medium}
                />
            }
        };
    }

    private getLoadingFailed = (): IMenuItem => {
        return {
            id: Resources.CrossAccountSearchLoadingFailed,
            text: Resources.CrossAccountSearchLoadingFailed
        };
    }

    private onActivate = (menuItem: IMenuItem): boolean | void => {
        const entityTypeUrlParam = this.props.selectedTabId.value;
        this.pageSource.navigateToAccount(menuItem.text as string, this.props.defaultSearchText.value as string, entityTypeUrlParam);
    }
}

VssComponent.register("search.crossAccountSearch", CrossAccountSearch);
